import { TmBaseComponent } from '@treasury/presentation';
import { html, nothing, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ReportFiltersVm } from '../data/report-filters.vm';
import { ReportMetaDataVm } from '../data/report-meta-data.vm';

const reservedReportNames: string[] = [
    'Current Day Balance',
    'Prior Day Balance',
    'Current Day Transaction',
    'Prior Day Transaction',
];

@customElement('custom-report-form')
export class CustomReportForm extends TmBaseComponent {
    @property({ attribute: false })
    public report?: ReportMetaDataVm;

    @property({ attribute: false })
    public filters?: ReportFiltersVm;

    protected verifiedPropNames: readonly (keyof this)[] = ['report', 'filters'];

    @state()
    private isNameValid = false;

    @state()
    private areFiltersValid = false;

    public render() {
        return html`
            <div style="min-width: 300px">
                <div class="flex flex-col">
                    <tm-text-field
                        label="Report Name"
                        .initial=${this.reportName}
                        required
                        @value-changed=${this.handleReportNameValueChange}
                    ></tm-text-field>
                </div>
                ${this.renderReservedNameMessage()}
                <report-filters
                    .loading=${false}
                    .filtersVisible=${true}
                    .report=${this.report}
                    .filters=${this.filters}
                    .hideActions=${true}
                    @filters-changed=${({ detail: { isValid } }: CustomEvent) => {
                        this.areFiltersValid = isValid;
                    }}
                    as-desktop
                ></report-filters>
            </div>
        `;
    }

    protected updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);
        if (_changedProperties.has('isNameValid') || _changedProperties.has('areFiltersValid')) {
            this.dispatchEvent(
                new CustomEvent('validity-changed', {
                    detail: this.isNameValid && this.areFiltersValid,
                    bubbles: true,
                    composed: true,
                })
            );
        }
    }

    private get reportName() {
        return this.report?.name.trim() ?? '';
    }

    private get isUsingReservedName() {
        return reservedReportNames.includes(this.reportName);
    }

    private renderReservedNameMessage() {
        if (this.isUsingReservedName) {
            return html`<div class="text-red-500">This report name is reserved.</div>`;
        }
        return nothing;
    }

    private handleReportNameValueChange(e: CustomEvent) {
        if (!this.report) {
            return;
        }

        const { value: name, isValid: isInputValid } = e.detail;
        this.report.name = name;
        this.isNameValid = isInputValid && !this.isUsingReservedName;
    }
}
